import {
  WIZARD_STEPS,
  type IdealItemDto,
  type SetupProgressDto,
  type StepGuideDto,
} from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { StepIntro } from './Illustration';
import { useTalk } from './Talk';
import { AddedItems } from './AddedItems';
import { BrainNotes } from './StepReview';
import { ItemBase } from './Labels';
import { LibrarySources } from './LibrarySources';

const OTHER = 'Altri programmi';

/** Chi usa uno strumento e chi ne risponde, dall'organizzazione ideale (ADR 0039). */
interface Owners {
  risponde: string[];
  usano: string[];
}

/** Dove si trova un software nella pianta: per tutta l'impresa, oppure in una sede per un'attività. */
interface Placed {
  v: IdealItemDto;
  dove: string | null;
  attivita: string | null;
}

const norm = (s: string) => s.trim().toLowerCase();
const initials = (s: string) =>
  s
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .slice(0, 3)
    .map((w) => w[0]!.toUpperCase())
    .join('') || s.slice(0, 3).toUpperCase();

/**
 * Passo «Programmi e strumenti» (ADR 0039, 0049): la pianta dei software dell'azienda ideale, non i programmi di oggi. In
 * cima i software centrali per area, sotto una scheda per ogni sede con i software di ogni attività e cosa scambiano con
 * il centro. Ogni software si apre su cosa copre, in quali fasi e per cosa serve, i dati che riceve e passa, chi ne
 * risponde e i prodotti veri più probabili per questa azienda (ADR 0137: un riferimento, non un consiglio).
 */
export function ToolsStep({
  next,
  onNext,
  progress,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
  progress: SetupProgressDto | undefined;
}) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  // I programmi più usati arrivano in un secondo tempo: si rilegge in silenzio finché la ricerca è in corso.
  const guide = useTenantQuery<StepGuideDto>(['guide', 'software'], '/onboarding/guide/software', {
    refetchInterval: (g) => (g?.ideale?.cercando ? 4000 : false),
  });
  // L'organizzazione si legge solo quando la persona ci è già arrivata: aprirla prima la farebbe progettare in anticipo.
  const orgReached =
    WIZARD_STEPS.indexOf(progress?.guidata.raggiunto ?? 'visura') >=
    WIZARD_STEPS.indexOf('organizzazione');
  const org = useTenantQuery<StepGuideDto>(
    ['guide', 'organizzazione'],
    '/onboarding/guide/organizzazione',
    { enabled: orgReached },
  );
  const refresh = useMutation({
    mutationFn: () =>
      api<StepGuideDto>(t('/onboarding/guide/software/refresh'), { method: 'POST' }),
    onSuccess: (g) => qc.setQueryData(['tenant', tenant.id, 'guide', 'software'], g),
  });
  const d = guide.data?.ideale ?? null;
  const central = d?.impresa?.voci ?? [];
  const sites = (d?.sedi ?? []).map((s) => ({
    ...s,
    attivita: s.attivita.filter((a) => a.voci.length),
  }));
  const local: Placed[] = sites.flatMap((s) =>
    s.attivita.flatMap((a) => a.voci.map((v) => ({ v, dove: s.sede_id, attivita: a.attivita }))),
  );
  const placed: Placed[] = [...central.map((v) => ({ v, dove: null, attivita: null })), ...local];
  const notes = [
    ...new Set([
      ...(d?.impresa?.note ?? []),
      ...(d?.sedi ?? []).flatMap((s) => [...(s.sede_in_se?.note ?? []), ...s.attivita.flatMap((a) => a.note)]),
    ]),
  ];
  const updating =
    refresh.isPending || !!d?.impresa?.generating || !!d?.sedi.some((s) => s.generating);
  const error = d?.impresa?.error ?? d?.sedi.find((s) => s.error)?.error ?? null;
  const owners = toolOwners(org.data);
  const known = new Set(placed.map((p) => norm(p.v.nome)));
  const law = new Set(placed.filter((p) => p.v.obbligatorio).map((p) => norm(p.v.nome))).size;
  const withTools = sites.filter((s) => s.attivita.length);

  // Il software aperto: la sua chiave e il riquadro sotto cui si apre (il centro o una sede).
  const [sel, setSel] = useState<{ key: string; where: string } | null>(null);
  const talk = useTalk();

  const keyOf = (p: Placed) => `${p.dove ?? 'impresa'}|${p.attivita ?? ''}|${norm(p.v.nome)}`;
  const toggle = (p: Placed, where: string) =>
    setSel((s) => (s?.key === keyOf(p) && s.where === where ? null : { key: keyOf(p), where }));
  /** Da un dato che passa al software che lo riceve: si apre la sua scheda dove si trova. */
  const jump = (name: string) => {
    const p = placed.find((x) => norm(x.v.nome) === norm(name));
    if (!p) return;
    const where = p.dove ?? 'impresa';
    setSel({ key: keyOf(p), where });
    setTimeout(
      () => document.getElementById(`tools-${where}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      50,
    );
  };
  const open = (where: string) => {
    const p = sel?.where === where ? placed.find((x) => keyOf(x) === sel.key) : undefined;
    return p ? (
      <Detail
        p={p}
        where={sites.find((s) => s.sede_id === p.dove)}
        who={owners.get(norm(p.v.nome)) ?? null}
        known={known}
        onJump={jump}
        onClose={() => setSel(null)}
      />
    ) : null;
  };
  const tile = (p: Placed, where: string, sub: string) => (
    <Tool
      key={keyOf(p)}
      v={p.v}
      site={!!p.dove}
      sub={sub}
      selected={sel?.key === keyOf(p) && sel.where === where}
      onClick={() => toggle(p, where)}
    />
  );

  const areas = [...new Set(central.map((v) => v.gruppo ?? OTHER))];

  return (
    <div className="space-y-6 pb-8 pt-2">
      <StepIntro step="software" title="Programmi e servizi online">
        <p className="mt-1 text-sm text-ink/60">
          Per ogni lavoro, il programma o il servizio online con cui farlo: quelli che usate già
          con il loro nome, gli altri da scegliere. Prima quelli di tutta l’impresa, poi sede per sede.
        </p>
      </StepIntro>
      <BrainNotes step="software" d={d} what="i programmi" />

      {placed.length > 0 && (
        <section className="space-y-3 rounded-3xl bg-white p-5 ring-1 ring-black/5">
          {!updating && (
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-600 text-[11px] text-white">
                ✓
              </span>
              I programmi della tua azienda sono progettati
            </p>
          )}
          <h2 className="text-xl font-semibold leading-snug">
            {central.length} {central.length === 1 ? 'programma' : 'programmi'} per
            tutta l’impresa
            {local.length > 0 && (
              <>
                {' '}
                e {local.length} nelle sedi, scelti attività per attività
              </>
            )}
            .
          </h2>
          <p className="text-sm text-ink/60">
            Ogni voce dice il lavoro e con cosa farlo: «Usate» se ho trovato il programma che usate già,
            «Servizio ufficiale» se la legge vuole quello di un ente, «Da scegliere» negli altri casi, con
            i prodotti più probabili per un’azienda come la tua.
            {d?.cercando && <> Li sto ancora cercando.</>}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat n={central.length} label="centrali" />
            {withTools.map((s) => (
              <Stat
                key={s.sede_id}
                n={s.attivita.reduce((n, a) => n + a.voci.length, 0)}
                label={`a ${s.luogo?.split(',')[0] ?? s.sede}`}
              />
            ))}
            {law > 0 && <Stat n={law} label="richiesti dalla legge" />}
          </div>
        </section>
      )}

      {placed.length === 0 && !updating && !error && (
        <p className="text-sm text-ink/60">
          Il progetto dei programmi non è ancora pronto.{' '}
          {isManager && (
            <button className="text-accent hover:underline" onClick={() => refresh.mutate()}>
              Progettali ora
            </button>
          )}
        </p>
      )}
      {updating && (
        <p className="text-center text-xs text-ink/50">Sto aggiornando i programmi…</p>
      )}
      {!updating && error && (
        <p className="text-sm text-red-700">
          Il progetto dei programmi non è riuscito: {ownerError(error)}{' '}
          {isManager && (
            <button className="text-accent hover:underline" onClick={() => refresh.mutate()}>
              Riprova
            </button>
          )}
        </p>
      )}
      {(refresh.error || guide.error) && (
        <ErrorText>{errorMessage(refresh.error ?? guide.error)}</ErrorText>
      )}

      {central.length > 0 && (
        <section id="tools-impresa" className="space-y-3">
          <div className="px-1">
            <h3 className="text-base font-semibold">Per tutta l’impresa</h3>
            <p className="text-xs text-ink/50">I programmi e i servizi che usa tutta l’azienda.</p>
          </div>
          <div className="space-y-4 rounded-3xl bg-white p-4 ring-2 ring-accent/20">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {areas.map((a) => (
                <div key={a} className="space-y-1.5">
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-ink/45">
                    {a}
                  </p>
                  {central
                    .filter((v) => (v.gruppo ?? OTHER) === a)
                    .map((v) => tile({ v, dove: null, attivita: null }, 'impresa', v.descrizione))}
                </div>
              ))}
            </div>
            {open('impresa')}
          </div>
        </section>
      )}

      {sites.length > 0 && central.length > 0 && (
        <section className="space-y-3">
          <div className="px-1">
            <h3 className="text-base font-semibold">Sede per sede</h3>
            <p className="text-xs text-ink/50">
              I programmi che servono alle attività di ogni sede, oltre a quelli di tutta l’impresa.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {sites.map((s) => {
              const mine = local.filter((p) => p.dove === s.sede_id);
              // Cosa la sede scambia con il centro: dai «riceve» e «passa» dei suoi software verso quelli centrali.
              const centralNames = new Map(central.map((v) => [norm(v.nome), v.nome]));
              const wires = new Map<string, string[]>();
              for (const p of mine)
                for (const f of [...(p.v.passa ?? []), ...(p.v.riceve ?? [])]) {
                  const c = centralNames.get(norm(f.voce));
                  if (c && f.dato) wires.set(c, [...new Set([...(wires.get(c) ?? []), f.dato])]);
                }
              return (
                <div
                  key={s.sede_id}
                  id={`tools-${s.sede_id}`}
                  className="space-y-3 rounded-3xl bg-amber-50/40 p-4 ring-1 ring-amber-200"
                >
                  <div>
                    <h4 className="font-semibold">
                      {s.sede}
                      {s.luogo && <span className="font-normal text-ink/50"> · {s.luogo}</span>}
                    </h4>
                    {s.addetti ? <p className="text-xs text-ink/50">{s.addetti} addetti</p> : null}
                  </div>
                  {s.attivita.length ? (
                    s.attivita.map((a) => (
                      <div key={a.attivita} className="space-y-1.5 border-t border-amber-200/70 pt-3">
                        <p className="text-sm font-semibold">
                          {a.attivita} <span className="font-normal text-ink/45">· attività</span>
                        </p>
                        {mine
                          .filter((p) => p.attivita === a.attivita)
                          .map((p) =>
                            tile(
                              p,
                              s.sede_id,
                              p.v.per?.length ? `per ${p.v.per.slice(0, 2).join(', ')}` : p.v.descrizione,
                            ),
                          )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-ink/55">
                      {s.generating ? 'Sto progettando i programmi di questa sede…' : 'Usa i programmi di tutta l’impresa, nessuno solo suo.'}
                    </p>
                  )}
                  {wires.size > 0 && (
                    <div className="space-y-0.5 border-t border-dashed border-amber-300/70 pt-2 text-xs text-ink/55">
                      <p className="font-medium text-ink/60">Con il centro</p>
                      {[...wires].map(([c, dati]) => (
                        <p key={c}>
                          ⇄{' '}
                          <button className="font-medium text-accent hover:underline" onClick={() => jump(c)}>
                            {c}
                          </button>
                          : {dati.join(', ')}
                        </p>
                      ))}
                    </div>
                  )}
                  {open(s.sede_id)}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {notes.length > 0 && (
        <details className="group text-xs text-ink/50">
          <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90">▸</span>
            Le ipotesi da cui sono partito · {notes.length}
          </summary>
          <ul className="mt-2 space-y-0.5 pl-5">
            {notes.map((n) => (
              <li key={n} className="list-disc">
                {n}
              </li>
            ))}
          </ul>
        </details>
      )}

      {placed.length > 0 && !updating && (
        <AddedItems
          step="software"
          intro="In Organizzazione e ruoli ogni ruolo dice con quali programmi lavora e di quali risponde. Se ne usate uno che non ho trovato, o ve ne serve un altro, aggiungilo: resta tuo, e io lo completo e lo collego agli altri."
          label="Aggiungi un programma"
          placeholder="Es. il programma delle paghe o il gestionale che usate"
          notePlaceholder="Es. lo usa l’amministrazione per le fatture"
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        {isManager && !updating && placed.length > 0 && (
          <button className="text-xs text-accent hover:underline" onClick={() => refresh.mutate()}>
            Rifai il progetto
          </button>
        )}
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
      </div>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="rounded-2xl bg-black/[0.03] px-3 py-2">
      <p className="text-xl font-semibold tabular-nums">{n}</p>
      <p className="text-xs text-ink/55">{label}</p>
    </div>
  );
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="ml-1 whitespace-nowrap rounded-md bg-violet-100 px-1.5 py-px align-middle text-[10.5px] font-semibold text-violet-700">
      {children}
    </span>
  );
}

/** Un software nella pianta: sigla, nome, una riga su cosa fa o per cosa c'è, i programmi più usati. */
function Tool({
  v,
  site,
  sub,
  selected,
  onClick,
}: {
  v: IdealItemDto;
  site: boolean;
  sub: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-expanded={selected}
      className={`grid w-full grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-x-2.5 rounded-xl bg-white px-2.5 py-2 text-left ring-1 transition ${selected ? 'ring-2 ring-accent' : 'ring-black/10 hover:ring-accent/40'}`}
    >
      <span
        className={`row-span-2 grid h-9 w-9 place-items-center rounded-lg font-mono text-[10.5px] font-medium ring-1 ${site ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-accent/5 text-accent ring-accent/20'}`}
      >
        {v.sigla || initials(v.nome)}
      </span>
      <span className="text-sm font-semibold leading-tight">
        {v.lavoro || v.nome}
        <ItemBase v={v} />
      </span>
      <span className="text-xs leading-snug text-ink/55">
        <With v={v} />
        {sub}
        {!v.usate && !v.servizio && v.diffusi?.length ? (
          <span className="mt-0.5 block text-accent">
            Probabili: {v.diffusi.slice(0, 3).map((x) => x.nome).join(' · ')}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/** La scheda di un software: cosa copre, dove e perché serve, i dati che scambia, chi ne risponde, i programmi più usati. */
function Detail({
  p,
  where,
  who,
  known,
  onJump,
  onClose,
}: {
  p: Placed;
  where: { sede: string; luogo: string | null } | undefined;
  who: Owners | null;
  known: Set<string>;
  onJump: (name: string) => void;
  onClose: () => void;
}) {
  const v = p.v;
  const flow = (label: string, xs: IdealItemDto['riceve']) =>
    xs?.length ? (
      <Fact label={label}>
        <ul className="space-y-0.5">
          {xs.map((f) => (
            <li key={`${f.voce}-${f.dato}`}>
              {f.dato && <span className="text-ink/60">{f.dato} · </span>}
              {known.has(norm(f.voce)) ? (
                <button className="text-accent hover:underline" onClick={() => onJump(f.voce)}>
                  {f.voce}
                </button>
              ) : (
                f.voce
              )}
            </li>
          ))}
        </ul>
      </Fact>
    ) : null;
  const dove = where
    ? [where.sede, where.luogo?.split(',')[0], p.attivita].filter(Boolean).join(' · ')
    : 'tutta l’impresa';
  return (
    <div className="space-y-3 rounded-2xl bg-white p-4 ring-2 ring-accent">
      <div className="flex items-start gap-3">
        <span
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl font-mono text-xs font-medium ring-1 ${p.dove ? 'bg-amber-50 text-amber-800 ring-amber-200' : 'bg-accent/5 text-accent ring-accent/20'}`}
        >
          {v.sigla || initials(v.nome)}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold">
            {v.lavoro || v.nome}
            <ItemBase v={v} detail />
          </h4>
          <p className="text-sm">
            <With v={v} link />
          </p>
          <p className="text-xs text-ink/55">
            {[v.gruppo, dove, v.quantita].filter(Boolean).join(' · ')}
          </p>
          <LibrarySources refs={v.libreria} fondata={v.fondata} conti={v.conti} />
        </div>
        <button aria-label="Chiudi" className="text-xl leading-none text-ink/40" onClick={onClose}>
          ×
        </button>
      </div>
      {v.descrizione && <p className="text-sm">{v.descrizione}</p>}
      {v.per?.length ? (
        <p className="flex flex-wrap gap-1">
          {v.per.map((x) => (
            <span key={x} className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800 ring-1 ring-amber-200">
              per {x}
            </span>
          ))}
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        {v.dettagli.length > 0 && (
          <Fact label="Cosa copre">
            <ul className="list-disc space-y-0.5 pl-4">
              {v.dettagli.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Fact>
        )}
        {v.fasi?.length ? <Fact label="In quali fasi">{v.fasi.join(' · ')}</Fact> : null}
        {who?.risponde.length ? <Fact label="Ne risponde">{who.risponde.join(', ')}</Fact> : null}
        {who?.usano.length ? <Fact label="Lo usano">{who.usano.join(', ')}</Fact> : null}
        {!who && v.gruppo && (
          <Fact label="Lo usa">
            {v.gruppo}
            <span className="block text-xs text-ink/50">Chi ne risponde lo dice Organizzazione e ruoli.</span>
          </Fact>
        )}
        {flow('Riceve', v.riceve)}
        {flow('Passa', v.passa)}
        {!v.usate && !v.servizio && v.diffusi?.length ? (
          <Fact label="I prodotti più probabili per voi">
            <ul className="space-y-1">
              {v.diffusi.slice(0, 4).map((x) => (
                <li key={x.nome}>
                  <a className="text-accent hover:underline" href={x.fonte} target="_blank" rel="noreferrer">
                    {x.nome}
                  </a>
                  {x.produttore && !x.nome.toLowerCase().includes(x.produttore.toLowerCase()) && (
                    <span className="text-ink/45"> · {x.produttore}</span>
                  )}
                  {x.perche && <span className="block text-xs text-ink/55">{x.perche}</span>}
                </li>
              ))}
            </ul>
            <span className="block text-xs text-ink/50">Un riferimento trovato con le fonti, non un consiglio.</span>
          </Fact>
        ) : null}
      </div>
      {(v.norma || v.perche) && (
        <p className="flex flex-wrap items-baseline gap-2 text-sm">
          {v.norma && (
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">{v.norma}</span>
          )}
          {v.perche && (
            <span>
              <span className="text-ink/50">Perché c’è:</span> {v.perche}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * Con cosa si fa il lavoro (ADR 0092): il programma che usate già, con la fonte; il servizio dell'ente che la legge
 * impone; altrimenti il tipo di programma, da scegliere.
 */
function With({ v, link = false }: { v: IdealItemDto; link?: boolean }) {
  const [label, name] = v.usate ? ['Usate', v.usate] : v.servizio ? ['Servizio ufficiale', v.servizio] : ['Da scegliere', v.nome];
  const href = v.usate && v.fonte_usate && /^https?:\/\//.test(v.fonte_usate) ? v.fonte_usate : null;
  return (
    <span className="block">
      <span className={v.usate ? 'text-emerald-700' : v.servizio ? 'text-violet-700' : 'text-ink/50'}>{label}:</span>{' '}
      <span className="font-medium text-ink/80">{name}</span>
      {link && v.usate && v.fonte_usate && (
        <span className="text-xs text-ink/45">
          {' · '}
          {href ? (
            <a className="hover:underline" href={href} target="_blank" rel="noreferrer">
              fonte
            </a>
          ) : (
            v.fonte_usate
          )}
        </span>
      )}
    </span>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl bg-black/[0.025] px-3 py-2 text-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/40">{label}</p>
      {children}
    </div>
  );
}

/** Da ogni ruolo dell'organizzazione ideale, chi usa ogni strumento e chi ne risponde (ADR 0039). */
function toolOwners(g: StepGuideDto | undefined): Map<string, Owners> {
  const out = new Map<string, Owners>();
  const d = g?.ideale;
  if (!d) return out;
  const roles = [
    ...(d.impresa?.voci ?? []),
    ...d.sedi.flatMap((s) => [...(s.sede_in_se?.voci ?? []), ...s.attivita.flatMap((a) => a.voci)]),
  ];
  for (const r of roles)
    for (const u of r.strumenti ?? []) {
      const o = out.get(norm(u.nome)) ?? { risponde: [], usano: [] };
      const list = u.risponde ? o.risponde : o.usano;
      if (!list.includes(r.nome)) list.push(r.nome);
      out.set(norm(u.nome), o);
    }
  return out;
}
